/**
 * Desktop Agent Types
 *
 * Type definitions for communication between web browser and local Desktop Agent
 */

// ===== MESSAGE TYPES =====

/**
 * Message sent from browser to Desktop Agent
 */
export interface DesktopAgentMessage {
  action: string;
  requestId: string;
  payload?: unknown;
}

/**
 * Response sent from Desktop Agent to browser
 */
export interface DesktopAgentResponse<T = unknown> {
  requestId: string;
  status: "success" | "error";
  data?: T;
  error?: string;
  details?: string;
}

// ===== ACTION TYPES =====

export type DesktopAgentAction =
  | "GET_PRINTERS"
  | "PRINT_RECEIPT"
  | "PRINT_TICKETS"
  | "PRINT_THERMAL_TICKETS"
  | "GET_AGENT_INFO";

// ===== PRINTER TYPES =====

/**
 * Common CUPS printer options returned by the system.
 * These are the options exposed in the printer info from electron's getPrinters().
 * @see https://www.cups.org/doc/spec-ipp.html
 */
export interface PrinterOptions {
  /** Number of copies to print */
  copies?: string;
  /** Device URI (e.g., "usb://BOCA%20SYSTEMS/46%20300?serial=...") */
  "device-uri"?: string;
  /** Finishing options */
  finishings?: string;
  /** Job cancel timeout in seconds */
  "job-cancel-after"?: string;
  /** Job hold setting */
  "job-hold-until"?: string;
  /** Job priority (1-100) */
  "job-priority"?: string;
  /** Job sheets setting */
  "job-sheets"?: string;
  /** Marker change time */
  "marker-change-time"?: string;
  /** Number-up (pages per sheet) */
  "number-up"?: string;
  /** Printer commands supported */
  "printer-commands"?: string;
  /** Printer info/description */
  "printer-info"?: string;
  /** Whether printer is accepting jobs */
  "printer-is-accepting-jobs"?: string;
  /** Whether printer is shared */
  "printer-is-shared"?: string;
  /** Whether printer is temporary */
  "printer-is-temporary"?: string;
  /** Printer location */
  "printer-location"?: string;
  /** Printer make and model */
  "printer-make-and-model"?: string;
  /** Printer state (3 = idle, 4 = printing, 5 = stopped) */
  "printer-state"?: string;
  /** Timestamp of last state change */
  "printer-state-change-time"?: string;
  /**
   * Reason for current printer state.
   * Common values: "none", "offline-report", "paused", "shutdown", "cups-offline", "timed-out"
   */
  "printer-state-reasons"?: string;
  /** Printer type flags */
  "printer-type"?: string;
  /** Printer URI */
  "printer-uri-supported"?: string;
  /** System driver info */
  system_driverinfo?: string;
  /** Allow additional unknown options */
  [key: string]: unknown;
}

/**
 * Printer information from the Desktop Agent
 */
export interface PrinterInfo {
  name: string;
  displayName: string;
  description: string;
  status: number;
  isDefault: boolean;
  options?: PrinterOptions;
}

/**
 * Parameters for printing a receipt
 */
export interface PrintReceiptParams {
  invoice: unknown; // Invoice object from API
  printerName: string;
  logo?: string;
}

/**
 * Parameters for printing tickets
 */
export interface PrintTicketsParams {
  tickets: Array<unknown>; // Ticket objects from API
  printerName: string;
  thermal?: boolean;
}

/**
 * Parameters for printing thermal tickets (BOCA printers)
 */
export interface PrintThermalTicketsParams {
  pdfUrl: string;
  printerName: string;
}

// ===== CONNECTION TYPES =====

/**
 * Connection state for the Desktop Agent
 */
export enum DesktopAgentConnectionState {
  DISCONNECTED = "disconnected",
  CONNECTING = "connecting",
  CONNECTED = "connected",
  RECONNECTING = "reconnecting",
  ERROR = "error",
}

/**
 * Desktop Agent information
 */
export interface DesktopAgentInfo {
  version: string;
  platform: string;
  ready: boolean;
}

// ===== EVENT TYPES =====

/**
 * Events emitted by DesktopAgentClient
 */
export type DesktopAgentClientEvent =
  | "connected"
  | "disconnected"
  | "reconnecting"
  | "error"
  | "message";

/**
 * Event payload types
 */
export interface DesktopAgentEventMap {
  connected: void;
  disconnected: void;
  reconnecting: { attempt: number };
  error: Error;
  message: DesktopAgentResponse;
}
